// AWS Certified AI Practitioner (AIF-C01) hub configuration
import flashcards from './flashcards.js';
import practice from './practice.js';
import objective from './objective.js';

const config = {
  slug: 'aws-ai-practitioner',
  vendor: 'Amazon Web Services',
  vendorTag: 'AWS · AI/ML Foundations',
  title: 'AWS·AI',
  titleAccent: '+',
  fullTitle: 'AI Practitioner',
  code: 'AIF-C01',
  channelTag: 'AWSAIF',
  threatPill: '● TARGET: AIF-C01',
  themeStorageKey: 'awsaif.theme',
  storagePrefix: 'awsaif',
  passPct: 80,
  practice: { totalSeconds: 90 * 60, passPct: 80 },
  objective: { totalSeconds: 30 * 60, passPct: 80 },
  domainNames: {
    D1: 'Fundamentals of AI and ML',
    D2: 'Fundamentals of Generative AI',
    D3: 'Applications of Foundation Models',
    D4: 'Guidelines for Responsible AI',
    D5: 'Security, Compliance, and Governance for AI',
  },
  data: { flashcards, practice, objective },

  domains: [
    {
      id: 'D1', title: 'Fundamentals of AI and ML', weight: '20%', weightPct: 20,
      intro: 'Covers the conceptual foundation of AI and ML — what types of problems each paradigm solves, how models are developed from data, and which AWS services map to those paradigms.',
      sections: [
        {
          heading: 'AI/ML Concepts',
          items: [
            { term: 'AI vs. ML vs. Deep Learning', desc: 'AI is the broad field; ML learns from data; DL uses neural networks to learn features automatically.' },
            { term: 'Supervised Learning', desc: 'Labeled input/output pairs train a model to predict on new data. Examples: regression, classification.' },
            { term: 'Unsupervised Learning', desc: 'Find hidden structure in unlabeled data. Examples: clustering, anomaly detection, dimensionality reduction.' },
            { term: 'Reinforcement Learning', desc: 'Agent takes actions in an environment to maximise a reward signal. Used in robotics and game-playing.' },
            { term: 'Semi-supervised & Self-supervised', desc: 'Leverage large amounts of unlabeled data alongside small labeled sets to improve model generalization.' },
          ],
        },
        {
          heading: 'ML Development Lifecycle',
          items: [
            { term: 'Problem Framing', desc: 'Define the business question, success metrics, and whether ML is the right approach.' },
            { term: 'Data Collection & Labeling', desc: 'Source, annotate, and store training data. Quality here determines model ceiling.' },
            { term: 'Feature Engineering', desc: 'Transform raw data into numeric representations the model can consume.' },
            { term: 'Model Training & Evaluation', desc: 'Fit on training set; evaluate on held-out validation and test sets using metrics like accuracy, F1, AUC-ROC.' },
            { term: 'Deployment & Monitoring', desc: 'Serve predictions at scale; detect drift and retrain when performance degrades.' },
          ],
        },
        {
          heading: 'AWS AI/ML Services',
          items: [
            { term: 'Amazon SageMaker', desc: 'End-to-end ML platform: notebooks, training, hyperparameter tuning, endpoints, pipelines.' },
            { term: 'Amazon Rekognition', desc: 'Pre-built computer vision for image and video analysis.' },
            { term: 'Amazon Comprehend', desc: 'NLP service for entity recognition, sentiment, key phrases, and topic modeling.' },
            { term: 'Amazon Forecast', desc: 'Managed time-series forecasting using ML.' },
            { term: 'Amazon Personalize', desc: 'Real-time recommendation engine.' },
            { term: 'Amazon Transcribe / Translate / Polly', desc: 'Speech-to-text, language translation, and text-to-speech respectively.' },
          ],
        },
      ],
    },
    {
      id: 'D2', title: 'Fundamentals of Generative AI', weight: '24%', weightPct: 24,
      intro: 'Covers how foundation models and large language models work, how they are deployed on AWS, and the concepts that govern their outputs — including tokenization, embeddings, temperature, and prompting.',
      sections: [
        {
          heading: 'Foundation Models & LLMs',
          items: [
            { term: 'Foundation Model (FM)', desc: 'A large model pre-trained on broad data that can be adapted to many downstream tasks via fine-tuning or prompting.' },
            { term: 'Large Language Model (LLM)', desc: 'A transformer-based FM pre-trained on text at internet scale. Generates coherent text by predicting next tokens.' },
            { term: 'Transformer Architecture', desc: 'Self-attention mechanism enables the model to weigh token relationships across the full context window.' },
            { term: 'Tokenization', desc: 'Input text is split into tokens (sub-words); the model works in token space, not character space.' },
            { term: 'Embeddings', desc: 'Dense numeric vectors that encode semantic meaning; similar concepts have nearby vectors.' },
            { term: 'Context Window', desc: 'Maximum tokens the model can process in one inference call. Longer context = higher cost.' },
          ],
        },
        {
          heading: 'Inference Controls',
          items: [
            { term: 'Temperature', desc: 'Controls output randomness. 0 = deterministic; higher values increase creativity and variation.' },
            { term: 'Top-p (nucleus) Sampling', desc: 'Samples from the smallest set of tokens whose cumulative probability ≥ p.' },
            { term: 'Top-k Sampling', desc: 'Restricts sampling to the k most probable next tokens.' },
            { term: 'Max Tokens / Stop Sequences', desc: 'Limit response length or terminate generation at specific strings.' },
          ],
        },
        {
          heading: 'AWS Generative AI Services',
          items: [
            { term: 'Amazon Bedrock', desc: 'Fully managed API for foundation models from Anthropic, Meta, Mistral, Amazon, and others — no GPU management required.' },
            { term: 'Amazon Titan Models', desc: 'AWS-native FMs for text generation, embeddings, and image generation available through Bedrock.' },
            { term: 'Amazon Q', desc: 'AWS generative AI assistant for business productivity and software development.' },
            { term: 'PartyRock', desc: 'No-code playground for building Bedrock-powered app prototypes.' },
            { term: 'Bedrock Knowledge Bases', desc: 'Managed RAG pipeline: ingests documents into a vector store, retrieves relevant chunks at inference time.' },
            { term: 'Bedrock Agents', desc: 'Orchestration layer that lets models invoke APIs, run code, and perform multi-step reasoning.' },
          ],
        },
        {
          heading: 'Prompt Engineering',
          items: [
            { term: 'Zero-shot Prompting', desc: 'Provide only the task description — no examples. Relies on the model\'s pre-trained knowledge.' },
            { term: 'Few-shot Prompting', desc: 'Include input-output examples in the prompt to steer behavior without fine-tuning.' },
            { term: 'Chain-of-Thought (CoT)', desc: 'Ask the model to reason step-by-step before giving the final answer. Improves complex reasoning tasks.' },
            { term: 'Retrieval-Augmented Generation (RAG)', desc: 'Augment the prompt with retrieved documents to ground responses in factual, up-to-date content.' },
            { term: 'System Prompt', desc: 'Top-level instructions that set model persona, constraints, and output format.' },
          ],
        },
      ],
    },
    {
      id: 'D3', title: 'Applications of Foundation Models', weight: '28%', weightPct: 28,
      intro: 'The largest domain — covers practical decisions around deploying and customizing FMs: choosing the right model, fine-tuning vs. RAG, evaluating output quality, and integrating FMs into real applications.',
      sections: [
        {
          heading: 'Model Selection & Deployment',
          items: [
            { term: 'Capability-Cost Trade-off', desc: 'Larger models are more capable but cost more per token; match model size to task requirements.' },
            { term: 'Latency Requirements', desc: 'Streaming responses reduce perceived latency; some use-cases need sub-second responses.' },
            { term: 'On-demand vs. Provisioned Throughput', desc: 'On-demand is pay-per-token; provisioned throughput reserves capacity for predictable, high-volume workloads.' },
            { term: 'Multi-modal Models', desc: 'Accept images, audio, or video as input in addition to text.' },
          ],
        },
        {
          heading: 'Customization Strategies',
          items: [
            { term: 'Fine-tuning', desc: 'Continue training on a domain-specific labeled dataset to shift model behavior. Requires more data and compute than prompting.' },
            { term: 'Instruction Fine-tuning', desc: 'Fine-tune on instruction-response pairs to improve the model\'s ability to follow directions.' },
            { term: 'RLHF (Reinforcement Learning from Human Feedback)', desc: 'Align model outputs with human preferences via a reward model trained on comparisons.' },
            { term: 'RAG vs. Fine-tuning', desc: 'RAG is better for dynamic, frequently-updated knowledge; fine-tuning is better for style, format, and domain language.' },
            { term: 'Distillation', desc: 'Train a smaller "student" model to mimic the output distribution of a larger "teacher" model.' },
          ],
        },
        {
          heading: 'Evaluation & Quality',
          items: [
            { term: 'ROUGE', desc: 'Measures overlap of n-grams between generated and reference text. Common for summarization.' },
            { term: 'BLEU', desc: 'Precision-based n-gram overlap metric for machine translation.' },
            { term: 'BERTScore', desc: 'Uses contextual embeddings to measure semantic similarity between generated and reference text.' },
            { term: 'Human Evaluation', desc: 'Raters assess fluency, coherence, factuality, and helpfulness. Most reliable but expensive.' },
            { term: 'LLM-as-Judge', desc: 'Use a capable LLM to rate outputs on defined criteria — scalable human proxy.' },
            { term: 'Hallucination', desc: 'Model generates plausible-sounding but false information. Mitigated with RAG and grounding.' },
          ],
        },
        {
          heading: 'Bedrock Advanced Features',
          items: [
            { term: 'Bedrock Model Evaluation', desc: 'Built-in tool to benchmark models on custom task datasets before deployment.' },
            { term: 'Bedrock Guardrails', desc: 'Content filters and topic blocks that prevent harmful or off-topic model responses.' },
            { term: 'Continued Pre-training', desc: 'Adapt a Bedrock model to a specific domain corpus without labeled I/O pairs.' },
          ],
        },
      ],
    },
    {
      id: 'D4', title: 'Guidelines for Responsible AI', weight: '14%', weightPct: 14,
      intro: 'Responsible AI governs how models are designed, evaluated, and monitored to ensure they are safe, fair, and trustworthy. Covers bias, fairness, transparency, and AWS governance tooling.',
      sections: [
        {
          heading: 'Core Responsible AI Principles',
          items: [
            { term: 'Fairness', desc: 'Ensure model predictions are equitable across demographic groups; measure and mitigate disparate impact.' },
            { term: 'Explainability / Interpretability', desc: 'Provide human-understandable reasons for model decisions. Critical for regulated industries.' },
            { term: 'Transparency', desc: 'Document training data sources, model architecture, and known limitations in model cards.' },
            { term: 'Privacy', desc: 'Protect personal data used in training and inference; apply differential privacy or data anonymization where needed.' },
            { term: 'Safety', desc: 'Prevent harmful, offensive, or dangerous outputs through content filtering and red-teaming.' },
            { term: 'Robustness', desc: 'Model should perform reliably under distributional shift, adversarial inputs, and edge cases.' },
          ],
        },
        {
          heading: 'Bias & Mitigation',
          items: [
            { term: 'Data Bias', desc: 'Training data that under-represents or misrepresents certain groups leads to biased predictions.' },
            { term: 'Sampling Bias', desc: 'Non-random data collection skews the distribution the model learns from.' },
            { term: 'Reporting Bias', desc: 'Only outcomes that get recorded appear in data; absent events are invisible to the model.' },
            { term: 'Pre-processing Mitigations', desc: 'Resampling, reweighting, and dataset augmentation to balance representation.' },
            { term: 'In-processing Mitigations', desc: 'Fairness constraints added to the training objective.' },
            { term: 'Post-processing Mitigations', desc: 'Threshold adjustment on model outputs to equalize metrics across groups.' },
          ],
        },
        {
          heading: 'AWS Responsible AI Tooling',
          items: [
            { term: 'Amazon SageMaker Clarify', desc: 'Detects bias in datasets and models; explains feature attributions using SHAP values.' },
            { term: 'Bedrock Guardrails', desc: 'Apply content policies to both input and output; block denied topics, PII, and toxic content.' },
            { term: 'SageMaker Model Monitor', desc: 'Detects data and model drift in real-time inference, triggering alerts when distributions shift.' },
            { term: 'Model Cards', desc: 'Structured documentation capturing intended use, training details, performance, and limitations.' },
          ],
        },
      ],
    },
    {
      id: 'D5', title: 'Security, Compliance, and Governance for AI', weight: '14%', weightPct: 14,
      intro: 'Covers the security controls, compliance frameworks, and governance practices specific to AI workloads on AWS — including identity, data protection, network isolation, and audit mechanisms.',
      sections: [
        {
          heading: 'Identity & Access for AI Services',
          items: [
            { term: 'IAM Roles for SageMaker', desc: 'Execution roles grant SageMaker permission to access S3, ECR, and other services on behalf of training jobs and endpoints.' },
            { term: 'Bedrock Resource Policies', desc: 'Control which principals can invoke specific FMs or knowledge bases through resource-based policies.' },
            { term: 'Least Privilege', desc: 'Grant only the permissions required; use IAM conditions to restrict access by region, VPC, and tag.' },
            { term: 'Service Control Policies (SCPs)', desc: 'Organization-level guardrails that cap maximum permissions across all accounts.' },
          ],
        },
        {
          heading: 'Data Protection',
          items: [
            { term: 'Encryption at Rest', desc: 'S3 SSE-S3, SSE-KMS; SageMaker model artifacts and volumes are encrypted with customer-managed KMS keys.' },
            { term: 'Encryption in Transit', desc: 'All AWS AI service API calls use TLS 1.2+; inter-node training traffic can use inter-container encryption.' },
            { term: 'Data Masking & Tokenization', desc: 'Remove or replace PII before training or before sending to an FM API.' },
            { term: 'Amazon Macie', desc: 'S3 data discovery service that automatically detects and classifies sensitive data (PII, credentials).' },
          ],
        },
        {
          heading: 'Network Isolation',
          items: [
            { term: 'VPC for SageMaker', desc: 'Training jobs and endpoints can run in a VPC without internet egress; data stays on the AWS network.' },
            { term: 'PrivateLink / Interface VPC Endpoints', desc: 'Access Bedrock, SageMaker, and S3 APIs privately without traversing the public internet.' },
            { term: 'Security Groups & NACLs', desc: 'Layer 4 controls that restrict inbound/outbound traffic to and from ML compute resources.' },
          ],
        },
        {
          heading: 'Audit, Monitoring & Governance',
          items: [
            { term: 'AWS CloudTrail', desc: 'Logs every API call to Bedrock, SageMaker, and IAM. Essential for audit trails and forensics.' },
            { term: 'Amazon CloudWatch', desc: 'Collects metrics, logs, and alarms from SageMaker endpoints and training jobs.' },
            { term: 'SageMaker Model Monitor', desc: 'Detects data drift and model quality degradation in deployed endpoints; creates governance baselines.' },
            { term: 'AWS Config', desc: 'Tracks resource configuration changes and evaluates against compliance rules.' },
            { term: 'Bedrock Model Invocation Logging', desc: 'Capture full input/output logs of FM API calls to S3 or CloudWatch for compliance review.' },
          ],
        },
        {
          heading: 'Compliance Frameworks',
          items: [
            { term: 'AWS Shared Responsibility Model', desc: 'AWS secures infrastructure; customers secure their data, IAM, and application logic.' },
            { term: 'GDPR / CCPA', desc: 'Privacy regulations requiring data minimization, subject access rights, and processing agreements.' },
            { term: 'HIPAA', desc: 'Healthcare data regulation; Bedrock and SageMaker are HIPAA-eligible with a signed BAA.' },
            { term: 'SOC 2 / ISO 27001', desc: 'Security certification programs applicable to AI workloads on AWS.' },
            { term: 'AWS Artifact', desc: 'On-demand access to AWS compliance reports and certifications.' },
          ],
        },
      ],
    },
  ],

  weightingMap: [
    { id: 'D1', weight: '20%', title: 'AI/ML Fundamentals', desc: 'Core concepts, paradigms, lifecycle, AWS AI services. ~2 study weeks.' },
    { id: 'D2', weight: '24%', title: 'Generative AI Fundamentals', desc: 'FMs, LLMs, Bedrock, prompting, RAG. ~3 study weeks.' },
    { id: 'D3', weight: '28%', title: 'Applying Foundation Models', desc: 'Largest domain. Model selection, fine-tuning, evaluation. ~3.5 study weeks.' },
    { id: 'D4', weight: '14%', title: 'Responsible AI', desc: 'Fairness, explainability, Clarify, Guardrails. ~1.5 study weeks.' },
    { id: 'D5', weight: '14%', title: 'Security & Governance', desc: 'IAM, encryption, VPC, audit, compliance. ~1.5 study weeks.' },
    { id: 'FINAL', weight: '100%', title: 'Mock + Review', desc: 'Practice exams + cheat sheet. Final sprint.' },
  ],
};

export default config;
